import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Импортируем все эндпоинты из модулей
import { authEndpoints, authTags } from "./auth";
import { webhookEndpoints, webhookTags } from "./webhooks";
import { paymentStatsEndpoints, paymentStatsTags } from "./paymentStats";
import { adminEndpoints, adminTags, GetGiftedUserSharesByUserIdRequest, DeletePurchasedSharesRequest, DeleteGiftedSharesRequest, GetPurchasedUserSharesByUserIdRequest } from "./admin";
import { searchEndpoints, searchTags } from "./search";
import { notificationEndpoints, notificationTags } from "./notifications";
import { marketplaceEndpoints, marketplaceTags } from "./marketplace";
import { bingxEndpoints, bingxTags } from "./bingx";
import { altynEndpoints, altynTags } from "./altyn";
import { friendlyPayEndpoints, friendlyPayTags } from "./friendlypay";
import {
    withdrawalReceiptEndpoints,
    withdrawalReceiptTags,
    WithdrawalReceiptSettings,
    WithdrawalReceiptSettingsResponse,
} from "./withdrawalReceipts";
import { ticketsEndpoints, ticketsTags } from "./tickets";
import { tokenEndpoints, tokenTags } from "./tokens";
import { marathonEndpoints, marathonTags } from "./marathon";
import { structureTags } from "./structure";
import { dataIntegrityEndpoints, dataIntegrityTags } from "./dataIntegrity";
import {
    messagingEndpoints,
    messagingTags,
    MessagingCampaignsResponse,
    MessagingCampaignMutationResponse,
    BatchStatusResponse,
    SendMillenniumNewsRequest,
    SendMillenniumNewsResponse,
    SendMillenniumBroadcastRequest,
    SendMillenniumBroadcastResponse,
    MillenniumBroadcastsResponse,
    MillenniumBroadcastDetailResponse,
} from "./messaging";
import { newsEndpoints, newsTags } from "./news";
import { withdrawalsEndpoints, withdrawalsTags } from "./withdrawals";
import { apiStatsEndpoints, apiStatsTags } from "./apiStats";
import { programsEndpoints, programsTags } from "./programs";
import { safeEndpoints, safeTags } from "./safe";
import { starControlEndpoints, starControlTags } from "./starControl";
import { charityControlEndpoints, charityControlTags } from "./charityControl";
import { userStatsEndpoints, userStatsTags } from "./userStats";
import {
    programCompareEndpoints,
    programCompareTags,
    ProgramCompareParams,
    ProgramCompareResponse,
    UpdateStarPayDateResponse,
} from "./programCompare";
import {
    bloggerEndpoints,
    bloggerTags,
    BloggerListResponse,
    BloggerMutationResponse,
    BloggerChartStatsResponse,
    BloggerInvitationChildrenParams,
    BloggerInvitationChildrenResponse,
    BloggerStatsParams,
    BloggerStatsResponse,
    UpdateBloggerParams,
} from "./bloggers";
import {
    tokenOperationsEndpoints,
    tokenOperationsTags,
} from "./tokenOperations";
import {
    gameBonusEndpoints,
    gameBonusTags,
    GameBonusParams,
    GameBonusFund,
    GameBonusHistory,
    GameBonusPayout,
    GameBonusGlobalStatsResponse,
    PaginatedResponse as GameBonusPaginatedResponse,
} from "./gameBonus";
import {
    teamMembersEndpoints,
    teamMembersTags,
    TeamMemberDto,
    TeamMemberPayload,
    TeamDepartmentDto,
    TeamDepartmentPayload,
} from "./teamMembers";
import {
    helpEndpoints,
    helpTags,
    HelpTreeParams,
    HelpTreeNode,
    HelpNodeDetail,
    HelpNodePayload,
} from "./help";
import {
    salaryPiggyBankEndpoints,
    salaryPiggyBankTags,
    SalaryPiggyBankUsersParams,
    SalaryPiggyBankPaymentsParams,
    SalaryPiggyBankSettings,
    SalaryPiggyBankUsersResponse,
    SalaryPiggyBankPaymentsResponse,
    SalaryPiggyBankMonthlyStats,
    SalaryPiggyBankHistoricalStatsResponse,
} from "./salaryPiggyBank";
import {
    advertisingFundEndpoints,
    advertisingFundTags,
    AdvFundUsersParams,
    AdvFundPaymentsParams,
    AdvFundAdminSettings,
    AdvFundUsersResponse,
    AdvFundPaymentsResponse,
    AdvFundMonthlyStats,
    AdvFundHistoricalStatsResponse,
    AdvFundChannel,
    CreateAdvFundChannelRequest,
    UpdateAdvFundChannelRequest,
    AdvFundExpenseRecord,
    AdvFundExpensesResponse,
    AdvFundExpensesParams,
    CreateAdvFundExpenseRequest,
} from "./advertisingFund";
import {
    incentiveFundEndpoints,
    incentiveFundTags,
    IncentiveFundSummary,
    IncentiveFundPaymentsResponse,
    IncentiveFundPayoutsResponse,
    IncentiveFundParams,
    CreateIncentiveFundPayoutRequest,
} from "./incentiveFund";
import {
    landingAnalyticsEndpoints,
    landingAnalyticsTags,
    LandingAnalyticsFilters,
    LandingAnalyticsEvent,
    LandingAnalyticsStats,
    LandingAnalyticsFilterOptions,
    LandingAnalyticsChartData,
    LandingVideoStat,
} from "./landingAnalytics";

// Импортируем типы
import {
    LoginRequest,
    RegisterRequest,
    RegisterInfoResponse,
    RegisterInfoRequest,
    LoginResponse,
    CrossAuthRequest,
    CrossAuthResponse,
    UpdateProfileRequest,
    Verify2FARequest,
} from "./auth";

import {
    Webhook,
    CreateWebhookRequest,
    UpdateWebhookRequest,
    WebhookEndpoint,
    CreateWebhookEndpointRequest,
    UpdateWebhookEndpointRequest,
    BulkCreateEndpointRequest,
    WebhookStatsSummary,
    WebhookEventType,
    WebhookFilters,
    WebhookEndpointFilters,
    WebhookLogFilters,
    PaginatedResponse,
    WebhookLog,
} from "../types/webhook";

import {
    PaymentLogFilters,
    PaymentLogsResponse,
    PaymentStatsResponse,
    PaymentLogDetailResponse,
    UserPaymentStatsResponse,
    PaymentLogExportResponse,
} from "../types/paymentStats";

import {
    TopUpUserRequest,
    TopUpUserTokenRequest,
    TopUpCharityRequest,
    GiftSharesRequest,
    UpdateSharesSettingsRequest,
    ChangeUserPasswordRequest,
    AuthorizeInvestmentUserRequest,
    RestoreMarathonUserAndTicketsRequest,
    SharesStatistics,
    SharesSettings,
    UserSharesSummary,
    UserShare,
} from "./admin";
import {
    PatronageDetailsResponse,
    PatronageUpdatePayload,
    PatronListParams,
    PatronListResponse,
} from "../types/patronage";

import {
    SearchUsersRequest,
    SearchCharityRequest,
    UserSearchResult,
    CharitySearchResult,
} from "./search";

import { NotificationItem, NotificationFilters } from "./notifications";
import { MarketplaceUrlResponse } from "./marketplace";
import { PendingBingxItem } from "./bingx";
import {
    FinancialStats,
    PaymentIncomeStats,
    WithdrawalStats,
    AllUsersStats,
    GameStatsItem,
    GameVipStatsData,
    PaymentIncomeListParams,
    PaymentIncomeListResponse,
    PaymentIncomeDetailsResponse,
    financialEndpoints,
    financialTags,
} from "./financial";
import {
    AdminAltynBalanceResponse,
    AdminAltynClientsParams,
    AdminAltynClientsResponse,
    CreateAdminAltynPayoutFromWithdrawalRequest,
    AdminAltynPaymentDetail,
    AdminAltynPaymentsParams,
    AdminAltynPaymentsResponse,
    AdminAltynPayoutsParams,
    AdminAltynPayoutsResponse,
    AdminAltynPayoutDetail,
    AdminAltynStatsResponse,
} from "./altyn";
import type {
    AdminFriendlyPayPaymentsParams,
    AdminFriendlyPayPaymentsResponse,
    AdminFriendlyPayPaymentDetail,
    AdminFriendlyPaySettingsResponse,
    AdminFriendlyPayStatsResponse,
    UpdateAdminFriendlyPaySettingsRequest,
} from "./friendlypay";
import {
    AdminTicketsParams,
    AdminTicketsResponse,
    AdminTicketsStatsResponse,
    AdminAwardTicketsPayload,
    AdminAwardTicketsResponse,
} from "./tickets";
import {
    AdamProxyStatsResponse,
    AdamProxyPaymentListParams,
    AdamProxyPaymentListResponse,
    AdamProxyPayoutListParams,
    AdamProxyPayoutListResponse,
    AdamProxyPayoutCreateRequest,
} from "../types/adamProxy";
import {
    TributeStatsResponse,
    TributePaymentListParams,
    TributePaymentListResponse,
    TributeSettingsResponse,
    TributeWithdrawalListResponse,
    TributeResetResponse,
    TributeWithdrawal,
} from "../types/tribute";
import {
    StripeStatsResponse,
    StripePaymentListParams,
    StripePaymentListResponse,
} from "../types/stripe";
import { PayBlagoTourizmTokensRequest } from "./tokens";
import {
    AdminNewsListResponse,
    AdminNewsDetailResponse,
    CreateAdminNewsRequestBody,
    UpdateAdminNewsRequestBody,
} from "./news";
import {
    SafeStatistics,
    SafeUsersResponse,
    SafeUsersRequest,
    SafeUserDetails,
} from "./safe";
import { StarControlApiResponse, StarControlParams } from "./starControl";
import { CharityControlResponse, CharityControlParams } from "./charityControl";
// Marathon types not imported since client modals removed; stats endpoints use generic types

// Создаем основной API
export const api = createApi({
    reducerPath: "api",
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_API_BASE_URL || "/api/v1",
        prepareHeaders: (headers, { getState, endpoint }) => {
            const apiKey = process.env.REACT_APP_API_KEY;
            if (apiKey) {
                headers.set("apiKey", apiKey);
            }
            headers.set("Accept", "application/json");

            // Добавляем токен авторизации если он есть
            const token =
                (getState() as any).auth.token || localStorage.getItem("token");
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }

            // Не устанавливаем Content-Type для FormData - браузер установит автоматически
            // RTK Query автоматически определяет FormData и не устанавливает Content-Type,
            // но на всякий случай проверяем, что мы не перезаписываем его

            return headers;
        },
    }),
    tagTypes: [
        ...authTags,
        ...webhookTags,
        ...paymentStatsTags,
        ...adminTags,
        ...searchTags,
        ...notificationTags,
        ...marketplaceTags,
        ...bingxTags,
        ...financialTags,
        ...altynTags,
        ...friendlyPayTags,
        ...withdrawalReceiptTags,
        ...ticketsTags,
        ...tokenTags,
        ...marathonTags,
        ...structureTags,
        ...dataIntegrityTags,
        ...messagingTags,
        ...newsTags,
        ...withdrawalsTags,
        ...apiStatsTags,
        ...programsTags,
        ...safeTags,
        ...starControlTags,
        ...charityControlTags,
        ...teamMembersTags,
        ...programCompareTags,
        ...bloggerTags,
        "Projects",
        "ProjectStats",
        "ProjectGallery",
        "ProjectSubscriptions",
        "ProjectPayments",
        "AutoReferralPrograms",
        "AutoReferralStats",
        "AutoReferralGallery",
        "AutoReferralSubscriptions",
        "AutoReferralPayments",
        "WalletInfo",
        "WalletTopUpMethods",
        "WalletTopUpHistory",
        "WalletTopUpStatus",
        "AdminPendingCardPayments",
        "AdminBalanceInfo",
        "AdminRussiaTopUps",
        "RussiaHands",
        "HandlerPendingPayments",
        "HandlerBalanceInfo",
        "SummaryStats",
        "CurrencyStats",
        ...userStatsTags,
        ...tokenOperationsTags,
        ...salaryPiggyBankTags,
        ...advertisingFundTags,
        ...helpTags,
        ...financialTags,
        "GameLogger",
        "AdamProxyPayments",
        "AdamProxyPayouts",
        "StripePayments",
        "TributePayments",
        ...incentiveFundTags,
        ...gameBonusTags,
        ...landingAnalyticsTags,
        // Добавим другие теги по мере необходимости
    ],
    endpoints: (builder) => ({
        // ==================== Auth Endpoints ====================
        login: builder.mutation<LoginResponse, LoginRequest>(
            authEndpoints.login
        ),
        loginAdminViaTelegram: builder.mutation<LoginResponse, number>(
            authEndpoints.loginAdminViaTelegram
        ),
        register: builder.mutation<LoginResponse, RegisterRequest>(
            authEndpoints.register
        ),
        getRegisterInfo: builder.query<
            RegisterInfoResponse,
            RegisterInfoRequest
        >(authEndpoints.getRegisterInfo),
        getCurrentUser: builder.query<LoginResponse["user"], void>(
            authEndpoints.getCurrentUser
        ),
        crossAuth: builder.mutation<CrossAuthResponse, CrossAuthRequest>(
            authEndpoints.crossAuth
        ),
        updateProfile: builder.mutation<
            LoginResponse["user"],
            UpdateProfileRequest
        >(authEndpoints.updateProfile),
        updateLanguage: builder.mutation<{ message: string }, string>(
            authEndpoints.updateLanguage
        ),
        deletePhoto: builder.mutation<{ message: string }, void>(
            authEndpoints.deletePhoto
        ),
        verify2FA: builder.mutation<LoginResponse, Verify2FARequest>(
            authEndpoints.verify2FA
        ),

        // ==================== Webhook Endpoints ====================
        getWebhooks: builder.query<PaginatedResponse<Webhook>, WebhookFilters>(
            webhookEndpoints.getWebhooks
        ),
        getWebhook: builder.query<{ data: Webhook }, number>(
            webhookEndpoints.getWebhook
        ),
        createWebhook: builder.mutation<
            { data: Webhook; message: string },
            CreateWebhookRequest
        >(webhookEndpoints.createWebhook),
        updateWebhook: builder.mutation<
            { data: Webhook; message: string },
            { id: number; data: UpdateWebhookRequest }
        >(webhookEndpoints.updateWebhook),
        deleteWebhook: builder.mutation<{ message: string }, number>(
            webhookEndpoints.deleteWebhook
        ),
        getWebhookLogs: builder.query<
            PaginatedResponse<WebhookLog>,
            { id: number; filters: WebhookLogFilters }
        >(webhookEndpoints.getWebhookLogs),
        testWebhook: builder.mutation<{ message: string }, number>(
            webhookEndpoints.testWebhook
        ),
        toggleWebhookStatus: builder.mutation<{ message: string }, number>(
            webhookEndpoints.toggleWebhookStatus
        ),
        getWebhookEventTypes: builder.query<
            { data: Record<string, WebhookEventType> },
            void
        >(webhookEndpoints.getWebhookEventTypes),
        getWebhookStats: builder.query<{ data: WebhookStatsSummary }, void>(
            webhookEndpoints.getWebhookStats
        ),

        // Webhook Endpoints (Global)
        getWebhookEndpoints: builder.query<
            PaginatedResponse<WebhookEndpoint>,
            WebhookEndpointFilters
        >(webhookEndpoints.getWebhookEndpoints),
        getWebhookEndpoint: builder.query<{ data: WebhookEndpoint }, number>(
            webhookEndpoints.getWebhookEndpoint
        ),
        createWebhookEndpoint: builder.mutation<
            { data: WebhookEndpoint; message: string },
            CreateWebhookEndpointRequest
        >(webhookEndpoints.createWebhookEndpoint),
        updateWebhookEndpoint: builder.mutation<
            { data: WebhookEndpoint; message: string },
            { id: number; data: UpdateWebhookEndpointRequest }
        >(webhookEndpoints.updateWebhookEndpoint),
        deleteWebhookEndpoint: builder.mutation<{ message: string }, number>(
            webhookEndpoints.deleteWebhookEndpoint
        ),
        getWebhookEndpointLogs: builder.query<
            PaginatedResponse<WebhookLog>,
            { id: number; filters: WebhookLogFilters }
        >(webhookEndpoints.getWebhookEndpointLogs),
        testWebhookEndpoint: builder.mutation<{ message: string }, number>(
            webhookEndpoints.testWebhookEndpoint
        ),
        toggleWebhookEndpointStatus: builder.mutation<
            { message: string },
            number
        >(webhookEndpoints.toggleWebhookEndpointStatus),
        getWebhookEndpointStats: builder.query<
            { data: WebhookStatsSummary },
            void
        >(webhookEndpoints.getWebhookEndpointStats),

        // ==================== Game Token Operations ====================
        getTokenOperations: builder.query<
            import("../types/tokenOperations").TokenOperationListResponse,
            import("../types/tokenOperations").TokenOperationFilters
        >(tokenOperationsEndpoints.getTokenOperations),
        getTokenOperationsActions: builder.query<string[], void>(
            tokenOperationsEndpoints.getTokenOperationsActions
        ),

        // Webhook Endpoints (Related to specific webhook)
        getWebhookEndpointsByWebhook: builder.query<
            PaginatedResponse<WebhookEndpoint>,
            { webhookId: number; filters: WebhookEndpointFilters }
        >(webhookEndpoints.getWebhookEndpointsByWebhook),
        createWebhookEndpointForWebhook: builder.mutation<
            { data: WebhookEndpoint; message: string },
            {
                webhookId: number;
                data: Omit<CreateWebhookEndpointRequest, "webhook_id">;
            }
        >(webhookEndpoints.createWebhookEndpointForWebhook),
        bulkCreateWebhookEndpoints: builder.mutation<
            { data: WebhookEndpoint[]; message: string },
            { webhookId: number; data: BulkCreateEndpointRequest }
        >(webhookEndpoints.bulkCreateWebhookEndpoints),

        // ==================== Payment Statistics Endpoints ====================
        getPaymentLogs: builder.query<PaymentLogsResponse, PaymentLogFilters>(
            paymentStatsEndpoints.getPaymentLogs
        ),
        getPaymentLogDetail: builder.query<PaymentLogDetailResponse, number>(
            paymentStatsEndpoints.getPaymentLogDetail
        ),
        getPaymentStatistics: builder.query<
            PaymentStatsResponse,
            { date_from?: string; date_to?: string }
        >(paymentStatsEndpoints.getPaymentStatistics),
        getUserPaymentStats: builder.query<
            UserPaymentStatsResponse,
            { userId: number; date_from?: string; date_to?: string }
        >(paymentStatsEndpoints.getUserPaymentStats),
        exportPaymentLogs: builder.query<
            PaymentLogExportResponse,
            PaymentLogFilters
        >(paymentStatsEndpoints.exportPaymentLogs),

        // ==================== Financial / Dashboard Endpoints ====================
        getFinancialStats: builder.query<{ data: FinancialStats }, void>(
            financialEndpoints.getFinancialStats
        ),
        getPaymentIncomeStats: builder.query<{ data: PaymentIncomeStats }, void>(
            financialEndpoints.getPaymentIncomeStats
        ),
        getWithdrawalStats: builder.query<{ data: WithdrawalStats }, void>(
            financialEndpoints.getWithdrawalStats
        ),
        getAllUsersStats: builder.query<{ data: AllUsersStats }, void>(
            financialEndpoints.getAllUsersStats
        ),
        getGameStats: builder.query<{ data: GameStatsItem[] }, void>(
            financialEndpoints.getGameStats
        ),
        getGameVipStats: builder.query<{ data: GameVipStatsData }, void>(
            financialEndpoints.getGameVipStats
        ),

        // ==================== Admin Endpoints ====================
        topUpUser: builder.mutation<
            { success: boolean; message: string },
            TopUpUserRequest
        >(adminEndpoints.topUpUser),
        topUpUserToken: builder.mutation<
            { success: boolean; message: string },
            TopUpUserTokenRequest
        >(adminEndpoints.topUpUserToken),
        topUpCharity: builder.mutation<
            { success: boolean; message: string },
            TopUpCharityRequest
        >(adminEndpoints.topUpCharity),
        giftShares: builder.mutation<
            { success: boolean; message: string },
            GiftSharesRequest
        >(adminEndpoints.giftShares),
        manualPayCreditShares: builder.mutation<
            { success: boolean; message: string },
            import("./admin").ManualCreditSharesPayRequest
        >(adminEndpoints.manualPayCreditShares),
        getSharesStatistics: builder.query<{ data: SharesStatistics }, void>(
            adminEndpoints.getSharesStatistics
        ),
        getSharesSettings: builder.query<{ data: SharesSettings }, void>(
            adminEndpoints.getSharesSettings
        ),
        updateSharesSettings: builder.mutation<
            { success: boolean; message: string },
            UpdateSharesSettingsRequest
        >(adminEndpoints.updateSharesSettings),
        getPurchasedUserShares: builder.query<
            { success: boolean; data: UserSharesSummary[] },
            void
        >(adminEndpoints.getPurchasedUserShares),
        getGiftedUserShares: builder.query<
            { success: boolean; data: UserSharesSummary[] },

            void
        >(adminEndpoints.getGiftedUserShares),
        getPurchasedUserSharesByUserId: builder.query<{ success: boolean; data: UserShare[] }, GetPurchasedUserSharesByUserIdRequest
            >({
                query: (data) => adminEndpoints.getPurchasedUserSharesByUserId.query(data)
            }),
        getGiftedUserSharesByUserId: builder.query<{ success: boolean; data: UserShare[] }, GetGiftedUserSharesByUserIdRequest
            >({
                query: (data) => adminEndpoints.getGiftedUserSharesByUserId.query(data)
            }),
        deletePurchasedShares: builder.mutation<
            { success: boolean; message: string },
            DeletePurchasedSharesRequest
        >({
                query: (data) => adminEndpoints.deletePurchasedShares.query(data)
        }),
        deleteGiftedShares: builder.mutation<
            { success: boolean; message: string },
            DeleteGiftedSharesRequest
        >({
            query: (data) => adminEndpoints.deleteGiftedShares.query(data)
        }),
        transferShares: builder.mutation<
            {
                success: boolean;
                message: string;
                data: {
                    transferred: number;
                    from_user_id: number;
                    to_user_id: number;
                };
            },
            {
                from_user_id: number;
                quantity: number;
                to_user_id: number;
                shares_type: 0 | 1;
            }
        >(adminEndpoints.transferShares),
        getCreditShares: builder.query<
            { success: boolean; data: import("./admin").CreditShare[] },
            void
        >(adminEndpoints.getCreditShares),
        getCreditShare: builder.query<
            { success: boolean; data: import("./admin").CreditShare },
            number
        >(adminEndpoints.getCreditShare),
        createCreditShare: builder.mutation<
            {
                success: boolean;
                message: string;
                data: import("./admin").CreditShare;
            },
            import("./admin").CreateCreditShareRequest
        >(adminEndpoints.createCreditShare),
        updateCreditShare: builder.mutation<
            { success: boolean; message: string; data: import("./admin").CreditShare },
            { userId: number; data: import("./admin").UpdateCreditShareRequest }
        >(adminEndpoints.updateCreditShare),
        deleteCreditShare: builder.mutation<
            { success: boolean; message: string },
            number
        >(adminEndpoints.deleteCreditShare),
        getPatrons: builder.query<PatronListResponse, PatronListParams>({
            ...adminEndpoints.getPatrons,
            providesTags: ["Patronage"],
        }),
        getPatronageDetails: builder.query<PatronageDetailsResponse, number>({
            ...adminEndpoints.getPatronageDetails,
            providesTags: ["Patronage"],
        }),
        updatePatronage: builder.mutation<
            PatronageDetailsResponse,
            { userId: number; data: PatronageUpdatePayload }
        >({
            ...adminEndpoints.updatePatronage,
            invalidatesTags: ["Patronage"],
        }),
        transferCreditShares: builder.mutation<
            {
                success: boolean;
                message: string;
                data: {
                    transferred: number;
                    from_user_id: number;
                    to_user_id: number;
                };
            },
            {
                from_user_id: number;
                quantity: number;
                to_user_id: number;
            }
        >(adminEndpoints.transferCreditShares),
        addDailyPercent: builder.mutation<
            { success: boolean; message: string },
            { daily_percent: number; type: number }
        >(adminEndpoints.addDailyPercent),
        changeUserPassword: builder.mutation<
            { success: boolean; message: string },
            ChangeUserPasswordRequest
        >(adminEndpoints.changeUserPassword),
        authorizeInvestmentUser: builder.mutation<
            { success: boolean; message: string },
            AuthorizeInvestmentUserRequest
        >(adminEndpoints.authorizeInvestmentUser),
        confirmWithdrawAll: builder.mutation<
            { success: boolean; message: string },
            void
        >(adminEndpoints.confirmWithdrawAll),
        confirmTransferAll: builder.mutation<
            { success: boolean; message: string },
            void
        >(adminEndpoints.confirmTransferAll),
        getInvestmentWithdrawRequests: builder.query<
            {
                success: boolean;
                data: {
                    requests: import("../types/investments").InvestmentWithdrawRequest[];
                    summary: import("../types/investments").InvestmentWithdrawSummary;
                };
            },
            void
        >(adminEndpoints.getInvestmentWithdrawRequests),
        getInvestmentTransferRequests: builder.query<
            {
                success: boolean;
                data: {
                    requests: import("../types/investments").InvestmentTransferRequest[];
                    summary: import("../types/investments").InvestmentTransferSummary;
                };
            },
            void
        >(adminEndpoints.getInvestmentTransferRequests),
        getDailyPercentStats: builder.query<
            import("./admin").GetInvestmentDailyStatsResponse,
            import("./admin").GetInvestmentStatsParams | undefined
        >({
            query: (params) => adminEndpoints.getDailyPercentStats.query(params || undefined),
            providesTags: ["InvestmentStats"],
        }),
        getWeeklyPercentStats: builder.query<
            import("./admin").GetInvestmentWeeklyStatsResponse,
            import("./admin").GetInvestmentStatsParams | undefined
        >({
            query: (params) => adminEndpoints.getWeeklyPercentStats.query(params || undefined),
            providesTags: ["InvestmentStats"],
        }),
        getAggregatedStrategyStats: builder.query<
            import("./admin").GetAggregatedStrategyStatsResponse,
            void
        >({
            query: () => adminEndpoints.getAggregatedStrategyStats.query(),
            providesTags: ["InvestmentStats"],
        }),
        getInvestments: builder.query<
            import("./admin").GetInvestmentsResponse,
            import("./admin").GetInvestmentsParams | undefined
        >(adminEndpoints.getInvestments),
        directWithdraw: builder.mutation<
            {
                success: boolean;
                message: string;
                data: {
                    user_id: number;
                    strategy: number;
                    amount: number;
                    commission: number;
                    net_amount: number;
                };
            },
            { user_id: number; strategy: number; amount: number }
        >(adminEndpoints.directWithdraw),
        getInvestmentStrategy: builder.query<
            import("./admin").GetInvestmentStrategyResponse,
            import("./admin").GetInvestmentStrategyParams
        >(adminEndpoints.getInvestmentStrategy),
        updateInvestmentStrategy: builder.mutation<
            import("./admin").UpdateInvestmentStrategyResponse,
            import("./admin").UpdateInvestmentStrategyParams
        >(adminEndpoints.updateInvestmentStrategy),
        confirmInvestmentWithdraw: builder.mutation<
            { success: boolean; message: string },
            { user_id: number; strategy: number }
        >(adminEndpoints.confirmInvestmentWithdraw),
        cancelInvestmentWithdraw: builder.mutation<
            { success: boolean; message: string },
            { user_id: number; strategy: number; comment?: string }
        >(adminEndpoints.cancelInvestmentWithdraw),
        confirmInvestmentTransfer: builder.mutation<
            { success: boolean; message: string },
            {
                user_id: number;
                from_strategy: number;
                to_strategy: number;
                amount: number;
            }
        >(adminEndpoints.confirmInvestmentTransfer),
        cancelInvestmentTransfer: builder.mutation<
            { success: boolean; message: string },
            {
                user_id: number;
                from_strategy: number;
                to_strategy: number;
                amount: number;
            }
        >(adminEndpoints.cancelInvestmentTransfer),
        goldControl: builder.mutation<
            { success: boolean; message: string; removed_count?: number },
            { gold_control: string }
        >(adminEndpoints.goldControl),
        getPendingJivo: builder.query<{ data: any[] }, void>(
            adminEndpoints.getPendingJivo
        ),
        confirmJivo: builder.mutation<
            { success: boolean; message: string },
            { jivo_id: number; code: string }
        >(adminEndpoints.confirmJivo),
        payMarathon: builder.mutation<
            { success: boolean; message: string },
            void
        >(adminEndpoints.payMarathon),
        restoreMarathonUserAndTickets: builder.mutation<
            { success: boolean; message: string },
            RestoreMarathonUserAndTicketsRequest
        >(adminEndpoints.restoreMarathonUserAndTickets),
        
        getStrategies: builder.query<{ success: boolean; data: import("./admin").InvestmentStrategy[] }, void>(
            adminEndpoints.getStrategies
        ),
        updateStrategy: builder.mutation<
            { success: boolean; message: string; data: import("./admin").InvestmentStrategy },
            { id: number; payload: import("./admin").UpdateInvestmentStrategyPayload }
        >(adminEndpoints.updateStrategy),

        // ==================== Search Endpoints ====================
        searchUsers: builder.query<
            { data: UserSearchResult[] },
            SearchUsersRequest
        >(searchEndpoints.searchUsers),
        searchCharity: builder.query<
            { data: CharitySearchResult[] },
            SearchCharityRequest
        >(searchEndpoints.searchCharity),

        // ==================== Notification Endpoints ====================
        getNotifications: builder.query<
            { data: NotificationItem[]; pagination?: any },
            NotificationFilters | void
        >(notificationEndpoints.getNotifications),
        markAsRead: builder.mutation<{ success: boolean }, number>(
            notificationEndpoints.markAsRead
        ),
        markAllAsRead: builder.mutation<{ success: boolean }, void>(
            notificationEndpoints.markAllAsRead
        ),
        getUnreadCount: builder.query<{ count: number }, void>(
            notificationEndpoints.getUnreadCount
        ),
        deleteNotification: builder.mutation<{ success: boolean }, number>(
            notificationEndpoints.deleteNotification
        ),

        // ==================== Marketplace Endpoints ====================
        getMarketplaceUrl: builder.query<MarketplaceUrlResponse, void>(
            marketplaceEndpoints.getMarketplaceUrl
        ),

        // ==================== Bingx Endpoints ====================
        getPendingBingx: builder.query<{ data: PendingBingxItem[] }, void>(
            bingxEndpoints.getPendingBingx
        ),
        confirmBingx: builder.mutation<{ success: boolean }, number>(
            bingxEndpoints.confirmBingx
        ),
        cancelBingxRequest: builder.mutation<{ success: boolean }, number>(
            bingxEndpoints.cancelBingxRequest
        ),

        // ==================== Crypto Wallets (BingX Accounts) ====================
        getBingXWallets: builder.query<any[], void>(
            bingxEndpoints.getBingXWallets
        ),
        createBingXWallet: builder.mutation<
            { success: boolean; message: string },
            any
        >(bingxEndpoints.createBingXWallet),
        updateBingXWallet: builder.mutation<
            { success: boolean; message: string },
            any
        >(bingxEndpoints.updateBingXWallet),
        deleteBingXWallet: builder.mutation<
            { success: boolean; message: string },
            number
        >(bingxEndpoints.deleteBingXWallet),
        syncBingXAddresses: builder.mutation<any, number>(
            bingxEndpoints.syncBingXAddresses
        ),
        getBingXBalance: builder.query<any, number>(
            bingxEndpoints.getBingXBalance
        ),
        getBingXWithdrawals: builder.query<
            { data: any[]; days: number },
            { id: number; days?: number }
        >(bingxEndpoints.getBingXWithdrawals),
        getBingXTopups: builder.query<
            { data: any[]; days: number; summary?: any },
            {
                status?: string;
                days?: number;
                network?: string;
                user_id?: string | number;
                account_id?: string | number;
            } | void
        >(bingxEndpoints.getBingXTopups),
        toggleBingXAddress: builder.mutation<
            any,
            { id: number; address_id: number; status: number }
        >(bingxEndpoints.toggleBingXAddress),
        addBingXAddress: builder.mutation<
            any,
            { id: number; network: string; address: string }
        >(bingxEndpoints.addBingXAddress),
        resetBingXSumTotal: builder.mutation<any, number>(
            bingxEndpoints.resetBingXSumTotal
        ),

        // ==================== Dashboard New Users Stats ====================
        getNewUsersStats: builder.query<
            {
                data: {
                    last24h: { total: number; paid_structure: number };
                    last7d: { total: number; paid_structure: number };
                    last30d: { total: number; paid_structure: number };
                };
            },
            void
        >({ query: () => "/admin/dashboard/new-users-stats" }),

        // ==================== Landing Analytics Endpoints ====================
        getLandingAnalyticsEvents: builder.query<{ success: boolean; data: { data: LandingAnalyticsEvent[]; current_page: number; last_page: number; total: number } }, LandingAnalyticsFilters>({
            ...landingAnalyticsEndpoints.getLandingAnalyticsEvents,
            providesTags: ["LandingAnalytics"],
        }),
        getLandingAnalyticsStats: builder.query<{ success: boolean; data: LandingAnalyticsStats }, LandingAnalyticsFilters>({
            ...landingAnalyticsEndpoints.getLandingAnalyticsStats,
            providesTags: ["LandingAnalytics"],
        }),
        getLandingAnalyticsFilterOptions: builder.query<{ success: boolean; data: LandingAnalyticsFilterOptions }, void>(
            landingAnalyticsEndpoints.getLandingAnalyticsFilterOptions
        ),
        getLandingAnalyticsChart: builder.query<{ success: boolean; data: LandingAnalyticsChartData[] }, { date_from?: string; date_to?: string }>({
            ...landingAnalyticsEndpoints.getLandingAnalyticsChart,
            providesTags: ["LandingAnalytics"],
        }),
        getLandingAnalyticsVideoStats: builder.query<{ success: boolean; data: LandingVideoStat[] }, { date_from?: string; date_to?: string }>({
            ...landingAnalyticsEndpoints.getLandingAnalyticsVideoStats,
            providesTags: ["LandingAnalytics"],
        }),

        // ==================== Payment Income List ====================
        getPaymentIncomeList: builder.query<
            PaymentIncomeListResponse,
            PaymentIncomeListParams | void
        >({
            query: (params) => financialEndpoints.getPaymentIncomeList.query(params || undefined),
            providesTags: ["PaymentIncomeList"],
        }),

        // ==================== Incentive Fund Endpoints ====================
        getIncentiveFundSummary: builder.query<IncentiveFundSummary, IncentiveFundParams>({
            ...incentiveFundEndpoints.getIncentiveFundSummary,
            transformResponse: (response: { data: IncentiveFundSummary }) => response.data,
        }),
        getIncentiveFundPayments: builder.query<IncentiveFundPaymentsResponse, IncentiveFundParams>({
            ...incentiveFundEndpoints.getIncentiveFundPayments,
            transformResponse: (response: { data: IncentiveFundPaymentsResponse }) => response.data,
        }),
        getIncentiveFundPayouts: builder.query<IncentiveFundPayoutsResponse, Omit<IncentiveFundParams, 'user_id' | 'program_id'>>({
            ...incentiveFundEndpoints.getIncentiveFundPayouts,
            transformResponse: (response: { data: IncentiveFundPayoutsResponse }) => response.data,
        }),
        createIncentiveFundPayout: builder.mutation<{ success: boolean; message: string }, CreateIncentiveFundPayoutRequest>(
            incentiveFundEndpoints.createIncentiveFundPayout
        ),

        // ==================== Game Bonus Endpoints ====================
        getGameBonusSummary: builder.query<GameBonusPaginatedResponse<GameBonusFund>, GameBonusParams>({
            ...gameBonusEndpoints.getGameBonusSummary,
            providesTags: ["GameBonus"],
        }),
        getGameBonusHistory: builder.query<GameBonusPaginatedResponse<GameBonusHistory>, GameBonusParams>({
            ...gameBonusEndpoints.getGameBonusHistory,
            providesTags: ["GameBonus"],
        }),
        getGameBonusPayouts: builder.query<GameBonusPaginatedResponse<GameBonusPayout>, GameBonusParams>({
            ...gameBonusEndpoints.getGameBonusPayouts,
            providesTags: ["GameBonus"],
        }),
        createGameBonusPayout: builder.mutation<any, { game_bonus_fund_id: number; fund_type: string; amount: number; destination?: string; comment?: string }>({
            ...gameBonusEndpoints.createGameBonusPayout,
            invalidatesTags: ["GameBonus"],
        }),
        getGameBonusGlobalStats: builder.query<GameBonusGlobalStatsResponse, void>(
            gameBonusEndpoints.getGameBonusGlobalStats
        ),

        // ==================== Payment Income Details ====================
        getPaymentIncomeDetails: builder.query<
            PaymentIncomeDetailsResponse,
            number
        >({
            query: (id) => financialEndpoints.getPaymentIncomeDetails.query(id),
            providesTags: ["PaymentIncomeDetails"],
        }),

        // ==================== Altyn (Admin) ====================
        getAdminAltynBalance: builder.query<AdminAltynBalanceResponse, void>(
            altynEndpoints.getAdminAltynBalance
        ),
        getAdminAltynStats: builder.query<AdminAltynStatsResponse, void>(
            altynEndpoints.getAdminAltynStats
        ),
        getAdminAltynPayments: builder.query<
            AdminAltynPaymentsResponse,
            AdminAltynPaymentsParams | void
        >({
            query: (params) => altynEndpoints.getAdminAltynPayments.query(params || undefined),
            providesTags: ["AltynPayments"],
        }),
        getAdminAltynClients: builder.query<
            AdminAltynClientsResponse,
            AdminAltynClientsParams | void
        >({
            query: (params) => altynEndpoints.getAdminAltynClients.query(params || undefined),
            providesTags: ["AltynClients"],
        }),
        getAdminAltynPayment: builder.query<
            { success: boolean; data: AdminAltynPaymentDetail },
            number
        >({
            query: (id) => altynEndpoints.getAdminAltynPayment.query(id),
            providesTags: ["AltynPayments"],
        }),
        getAdminAltynPayouts: builder.query<
            AdminAltynPayoutsResponse,
            AdminAltynPayoutsParams | void
        >({
            query: (params) => altynEndpoints.getAdminAltynPayouts.query(params || undefined),
            providesTags: ["AltynPayouts"],
        }),
        getAdminAltynPayout: builder.query<
            { success: boolean; data: AdminAltynPayoutDetail },
            number
        >({
            query: (id) => altynEndpoints.getAdminAltynPayout.query(id),
            providesTags: ["AltynPayouts"],
        }),
        checkAdminAltynPayout: builder.mutation<
            { success: boolean; data: { id: number; status_code: number | null; status_text: string | null; processing_data: any; updated_at: string | null } },
            number
        >({
            query: (id) => altynEndpoints.checkAdminAltynPayout.query(id),
            invalidatesTags: ["AltynPayouts"],
        }),
        createAdminAltynCryptoPayout: builder.mutation<
            { success: boolean; message: string; data: any },
            CreateAdminAltynPayoutFromWithdrawalRequest
        >({
            query: (body) => altynEndpoints.createAdminAltynCryptoPayout.query(body),
            invalidatesTags: ["AltynPayouts", "PendingWithdrawals"],
        }),
        createAdminAltynSbpPayout: builder.mutation<
            { success: boolean; message: string; data: any },
            CreateAdminAltynPayoutFromWithdrawalRequest
        >({
            query: (body) => altynEndpoints.createAdminAltynSbpPayout.query(body),
            invalidatesTags: ["AltynPayouts", "PendingWithdrawals"],
        }),

        // ==================== FriendlyPay (Admin) ====================
        getAdminFriendlyPaySettings: builder.query<
            AdminFriendlyPaySettingsResponse,
            void
        >(friendlyPayEndpoints.getAdminFriendlyPaySettings),
        updateAdminFriendlyPaySettings: builder.mutation<
            AdminFriendlyPaySettingsResponse,
            UpdateAdminFriendlyPaySettingsRequest
        >(friendlyPayEndpoints.updateAdminFriendlyPaySettings),
        getWithdrawalReceiptSettings: builder.query<
            WithdrawalReceiptSettingsResponse,
            void
        >(withdrawalReceiptEndpoints.getWithdrawalReceiptSettings),
        updateWithdrawalReceiptSettings: builder.mutation<
            WithdrawalReceiptSettingsResponse,
            WithdrawalReceiptSettings
        >(withdrawalReceiptEndpoints.updateWithdrawalReceiptSettings),
        getAdminFriendlyPayStats: builder.query<
            AdminFriendlyPayStatsResponse,
            void
        >(friendlyPayEndpoints.getAdminFriendlyPayStats),
        getAdminFriendlyPayPayments: builder.query<
            AdminFriendlyPayPaymentsResponse,
            AdminFriendlyPayPaymentsParams | void
        >({
            query: (params) => friendlyPayEndpoints.getAdminFriendlyPayPayments.query(params || undefined),
            providesTags: ["FriendlyPayPayments"],
        }),
        getAdminFriendlyPayPayment: builder.query<
            { success: boolean; data: AdminFriendlyPayPaymentDetail },
            number
        >({
            query: (id) => friendlyPayEndpoints.getAdminFriendlyPayPayment.query(id),
            providesTags: ["FriendlyPayPayments"],
        }),
        createAltynCryptoPayout: builder.mutation<
            { success: boolean; data: any },
            { amount: string; currency: string; wallet_address: string; rate_id?: string }
        >({
            query: (body) => ({
                url: "/topup/altyn/payout/crypto",
                method: "POST",
                body,
            }),
            invalidatesTags: ["AltynPayouts"],
        }),

        // ==================== Admin Tickets ====================
        getAdminTicketsStats: builder.query<AdminTicketsStatsResponse, void>({
            query: () => ticketsEndpoints.getAdminTicketsStats.query(),
            providesTags: ["Tickets"],
        }),
        getAdminTickets: builder.query<
            AdminTicketsResponse,
            AdminTicketsParams | void
        >({
            query: (params) => ticketsEndpoints.getAdminTickets.query(params || undefined),
            providesTags: ["Tickets"],
        }),
        awardTickets: builder.mutation<
            AdminAwardTicketsResponse,
            AdminAwardTicketsPayload
        >({
            query: (payload) => ticketsEndpoints.awardTickets.mutation(payload),
            invalidatesTags: ["Tickets"],
        }),

        // ==================== Adam Proxy Stripe Payments Stats ====================
        getAdamProxyPaymentStats: builder.query<
            AdamProxyStatsResponse,
            { date_from?: string; date_to?: string } | undefined
        >({
            query: (params) => ({
                url: "/admin/adam-proxy-payments/stats",
                method: "GET",
                params: (params ? { ...params } : undefined) as Record<string, any> | undefined,
            }),
            providesTags: ["AdamProxyPayments"],
        }),
        getAdamProxyPaymentList: builder.query<
            AdamProxyPaymentListResponse,
            AdamProxyPaymentListParams | undefined
        >({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params?.status) {
                    queryParams.append('status', params.status);
                }
                if (params?.date_from) {
                    queryParams.append('date_from', params.date_from);
                }
                if (params?.date_to) {
                    queryParams.append('date_to', params.date_to);
                }
                if (params?.search) {
                    queryParams.append('search', params.search);
                }
                if (params?.per_page) {
                    queryParams.append('per_page', params.per_page.toString());
                }
                if (params?.page) {
                    queryParams.append('page', params.page.toString());
                }
                if (params?.sort_by) {
                    queryParams.append('sort_by', params.sort_by);
                }
                if (params?.sort_order) {
                    queryParams.append('sort_order', params.sort_order);
                }
                const queryString = queryParams.toString();
                return `/admin/adam-proxy-payments/list${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ["AdamProxyPayments"],
        }),
        getAdamProxyPayoutList: builder.query<
            AdamProxyPayoutListResponse,
            AdamProxyPayoutListParams | undefined
        >({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params?.per_page) {
                    queryParams.append("per_page", params.per_page.toString());
                }
                if (params?.page) {
                    queryParams.append("page", params.page.toString());
                }
                if (params?.sort_by) {
                    queryParams.append("sort_by", params.sort_by);
                }
                if (params?.sort_order) {
                    queryParams.append("sort_order", params.sort_order);
                }
                const queryString = queryParams.toString();
                return `/admin/adam-proxy-payouts${queryString ? `?${queryString}` : ""}`;
            },
            providesTags: ["AdamProxyPayouts"],
        }),
        createAdamProxyPayout: builder.mutation<
            { success: boolean; data: import("../types/adamProxy").AdamProxyPayout },
            AdamProxyPayoutCreateRequest
        >({
            query: (body) => ({
                url: "/admin/adam-proxy-payouts",
                method: "POST",
                body,
            }),
            invalidatesTags: ["AdamProxyPayments", "AdamProxyPayouts"],
        }),

        // ==================== ALNY Stripe Payments Stats ====================
        getStripePaymentStats: builder.query<
            StripeStatsResponse,
            { date_from?: string; date_to?: string; source?: string } | undefined
        >({
            query: (params) => ({
                url: "/admin/stripe-payments/stats",
                method: "GET",
                params: (params ? { ...params } : undefined) as Record<string, any> | undefined,
            }),
            providesTags: ["StripePayments"],
        }),
        getStripePaymentList: builder.query<
            StripePaymentListResponse,
            StripePaymentListParams | undefined
        >({
            query: (params) => ({
                url: "/admin/stripe-payments/list",
                method: "GET",
                params: (params ? { ...params } : undefined) as Record<string, any> | undefined,
            }),
            providesTags: ["StripePayments"],
        }),

        // ==================== Tribute Payments Stats ====================
        getTributePaymentSettings: builder.query<TributeSettingsResponse, void>({
            query: () => ({
                url: "/admin/tribute-payments/settings",
                method: "GET",
            }),
            providesTags: ["TributePayments"],
        }),
        updateTributePaymentSettings: builder.mutation<
            TributeSettingsResponse,
            { commission_percent?: number; stats_from?: string | null }
        >({
            query: (body) => ({
                url: "/admin/tribute-payments/settings",
                method: "PUT",
                body,
            }),
            invalidatesTags: ["TributePayments"],
        }),
        resetTributeStats: builder.mutation<
            TributeResetResponse,
            { amount?: number; description?: string }
        >({
            query: (body) => ({
                url: "/admin/tribute-payments/reset",
                method: "POST",
                body,
            }),
            invalidatesTags: ["TributePayments"],
        }),
        getTributePaymentStats: builder.query<
            TributeStatsResponse,
            | {
                  date_from?: string;
                  date_to?: string;
                  source?: string;
                  purpose?: string;
              }
            | undefined
        >({
            query: (params) => ({
                url: "/admin/tribute-payments/stats",
                method: "GET",
                params: (params
                    ? Object.fromEntries(
                          Object.entries(params).filter(
                              ([, value]) => value !== undefined && value !== ""
                          )
                      )
                    : undefined) as Record<string, any> | undefined,
            }),
            providesTags: ["TributePayments"],
        }),
        getTributePaymentList: builder.query<
            TributePaymentListResponse,
            TributePaymentListParams | undefined
        >({
            query: (params) => ({
                url: "/admin/tribute-payments/list",
                method: "GET",
                params: (params
                    ? Object.fromEntries(
                          Object.entries(params).filter(
                              ([, value]) => value !== undefined && value !== ""
                          )
                      )
                    : undefined) as Record<string, any> | undefined,
            }),
            providesTags: ["TributePayments"],
        }),
        getTributeWithdrawals: builder.query<TributeWithdrawalListResponse, void>({
            query: () => ({
                url: "/admin/tribute-payments/withdrawals",
                method: "GET",
            }),
            providesTags: ["TributePayments"],
        }),
        createTributeWithdrawal: builder.mutation<
            { success: boolean; data: TributeWithdrawal },
            { amount: number; description?: string; withdrawn_at?: string }
        >({
            query: (body) => ({
                url: "/admin/tribute-payments/withdrawals",
                method: "POST",
                body,
            }),
            invalidatesTags: ["TributePayments"],
        }),
        updateTributeWithdrawal: builder.mutation<
            { success: boolean; data: TributeWithdrawal },
            { id: number; amount?: number; description?: string; withdrawn_at?: string }
        >({
            query: ({ id, ...body }) => ({
                url: `/admin/tribute-payments/withdrawals/${id}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["TributePayments"],
        }),

        // ==================== Token Endpoints ====================
        payBlagoTourizmTokens: builder.mutation<
            { success: boolean; message: string },
            PayBlagoTourizmTokensRequest
        >(tokenEndpoints.payBlagoTourizmTokens),

        // ==================== Messaging Endpoints ====================
        getTelegramBots: builder.query<
            {
                success: boolean;
                data: Array<{
                    key: string;
                    name: string;
                    is_default: boolean;
                }>;
            },
            void
        >(messagingEndpoints.getTelegramBots),
        translateMessagingMessage: builder.mutation<
            { success: boolean; html?: string; message?: string },
            {
                html: string;
                from?: string;
                to: string;
                message_format?: string;
            }
        >(messagingEndpoints.translateMessage),
        sendMillenniumNews: builder.mutation<
            SendMillenniumNewsResponse,
            SendMillenniumNewsRequest | FormData
        >({
            ...messagingEndpoints.sendMillenniumNews,
            invalidatesTags: ["AdminMessaging"],
        }),
        sendMillenniumBroadcast: builder.mutation<
            SendMillenniumBroadcastResponse,
            SendMillenniumBroadcastRequest
        >({
            ...messagingEndpoints.sendMillenniumBroadcast,
            invalidatesTags: ["MillenniumBroadcasts"],
        }),
        listMillenniumBroadcasts: builder.query<
            MillenniumBroadcastsResponse,
            { phase?: string; refresh?: boolean } | void
        >({
            ...messagingEndpoints.listMillenniumBroadcasts,
            providesTags: ["MillenniumBroadcasts"],
        }),
        showMillenniumBroadcast: builder.query<
            MillenniumBroadcastDetailResponse,
            number
        >({
            ...messagingEndpoints.showMillenniumBroadcast,
            providesTags: ["MillenniumBroadcasts"],
        }),
        endMillenniumBroadcast: builder.mutation<
            MillenniumBroadcastDetailResponse,
            number
        >({
            ...messagingEndpoints.endMillenniumBroadcast,
            invalidatesTags: ["MillenniumBroadcasts"],
        }),
        startMillenniumBroadcast: builder.mutation<
            MillenniumBroadcastDetailResponse,
            number
        >({
            ...messagingEndpoints.startMillenniumBroadcast,
            invalidatesTags: ["MillenniumBroadcasts"],
        }),
        patchMillenniumBroadcast: builder.mutation<
            MillenniumBroadcastDetailResponse,
            {
                id: number;
                recording_url?: string;
                title?: string;
                starts_at?: string;
                ends_at?: string;
            }
        >({
            ...messagingEndpoints.patchMillenniumBroadcast,
            invalidatesTags: ["MillenniumBroadcasts"],
        }),
        destroyMillenniumBroadcast: builder.mutation<
            { success: boolean; message?: string },
            number
        >({
            ...messagingEndpoints.destroyMillenniumBroadcast,
            invalidatesTags: ["MillenniumBroadcasts"],
        }),
        sendMassMessage: builder.mutation<
            { users_count: number; bots_count?: number; jobs_count?: number; batch_id: string },
            {
                 message?: string;
                 category: number;
                 bot_keys: string[];
                 zoom?: boolean;
                 zoom_url?: string;
                 zoom_code?: string;
                 photo?: File;
                 video?: File;
            } | FormData
        >(messagingEndpoints.sendMassMessage),
        getBatchStatus: builder.query<BatchStatusResponse, string>(
            messagingEndpoints.getBatchStatus,
        ),
        cancelMassMessageBatch: builder.mutation<
            {
                success: boolean;
                message: string;
                data?: { processed: number; total: number };
            },
            string
        >(messagingEndpoints.cancelBatch),
        getMessagingCampaigns: builder.query<MessagingCampaignsResponse, string | void>(
            {
                ...messagingEndpoints.listCampaigns,
                providesTags: ["AdminMessaging"],
            }
        ),
        storeMessagingCampaign: builder.mutation<
            MessagingCampaignMutationResponse,
            FormData
        >({
            ...messagingEndpoints.storeCampaign,
            invalidatesTags: ["AdminMessaging"],
        }),
        updateMessagingCampaign: builder.mutation<
            MessagingCampaignMutationResponse,
            { id: number; body: FormData }
        >({
            ...messagingEndpoints.updateCampaign,
            invalidatesTags: ["AdminMessaging"],
        }),
        cancelMessagingCampaign: builder.mutation<
            MessagingCampaignMutationResponse,
            number
        >({
            ...messagingEndpoints.cancelCampaign,
            invalidatesTags: ["AdminMessaging"],
        }),
        deleteMessagingCampaign: builder.mutation<
            { success: boolean; message?: string },
            number
        >({
            ...messagingEndpoints.deleteCampaign,
            invalidatesTags: ["AdminMessaging"],
        }),
        clearMessagingHistory: builder.mutation<
            { success: boolean; message?: string; deleted?: number },
            void
        >({
            ...messagingEndpoints.clearHistory,
            invalidatesTags: ["AdminMessaging"],
        }),

        // ==================== Marathon Statistics ====================
        getMarathonStatistics: builder.query<any, { round?: number; type?: number }>(
            marathonEndpoints.getMarathonStatistics
        ),
        getMarathonTypes: builder.query<any, void>(
            marathonEndpoints.getMarathonTypes
        ),
        importMarathonResults: builder.mutation<
            { imported_count: number },
            { round: number; type: number; csv_text: string }
        >(marathonEndpoints.importMarathonResults),
        clearMarathonStatistics: builder.mutation<
            { deleted_count: number },
            { round?: number; type?: number }
        >(marathonEndpoints.clearMarathonStatistics),
        exportMarathonStatistics: builder.query<
            { data: string; filename: string },
            { round?: number; type?: number }
        >(marathonEndpoints.exportMarathonStatistics),
        getTicketsForExport: builder.query<Blob, { type: number }>({
            query: (params: { type: number }) => ({
                ...marathonEndpoints.getTicketsForExport.query(params),
                responseHandler: (response: Response) => response.blob(),
            }),
        }),

        // ==================== Structure/Tree Endpoints ====================
        checkTreeIntegrity: builder.mutation<
            {
                success: boolean;
                message: string;
                data?: {
                    blocked_nodes_count: number;
                    nodes_with_blocked_ancestor_count: number;
                    orphans_count: number;
                    cycles_count: number;
                    samples: {
                        blocked_nodes: number[];
                        nodes_with_blocked_ancestor: (number | null)[];
                        orphans: number[];
                        cycles: (number | null)[];
                    };
                };
            },
            void
        >({
            query: () => ({
                url: "/admin/special/check-tree-integrity",
                method: "POST",
            }),
        }),

        getTreeUserDetails: builder.mutation<
            {
                success: boolean;
                data?: {
                    user: {
                        id: number;
                        email?: string;
                        surname?: string;
                        name?: string;
                        is_blocked?: boolean;
                        inviter_id?: number;
                        created_at?: string;
                    };
                    path: number[];
                    blocked_ancestors: {
                        id: number;
                        surname?: string;
                        name?: string;
                        is_blocked: boolean;
                    }[];
                };
            },
            number
        >({
            query: (user_id) => ({
                url: "/admin/special/tree-user-details",
                method: "POST",
                body: { user_id },
            }),
        }),

        // ==================== Data Integrity Endpoints ====================
        verifyStatsIntegrity: builder.mutation<
            {
                success: boolean;
                data: {
                    total_users: number;
                    processed_users: number;
                    errors: any[];
                    has_more: boolean;
                    next_offset: number | null;
                };
            },
            { offset?: number; limit?: number }
        >(dataIntegrityEndpoints.verifyStatsIntegrity),
        fixStatsIntegrityUser: builder.mutation<
            { success: boolean; message: string },
            number
        >(dataIntegrityEndpoints.fixStatsIntegrityUser),
        getStatsIntegrityUserDetails: builder.query<
            { data: any },
            { user_id: number; currency: "USD" | "USDT" | 0 | 1 }
        >(dataIntegrityEndpoints.getStatsIntegrityUserDetails),

        // ==================== Admin News Endpoints ====================
        getAdminNews: builder.query<
            AdminNewsListResponse,
            {
                page?: number;
                per_page?: number;
                news_category_id?: number;
                is_published?: boolean;
            }
        >(newsEndpoints.getAdminNews),
        getAdminNewsItem: builder.query<AdminNewsDetailResponse, number>(
            newsEndpoints.getAdminNewsItem
        ),
        createAdminNews: builder.mutation<
            AdminNewsDetailResponse,
            CreateAdminNewsRequestBody
        >(newsEndpoints.createAdminNews),
        updateAdminNews: builder.mutation<
            AdminNewsDetailResponse,
            { id: number; data: UpdateAdminNewsRequestBody }
        >(newsEndpoints.updateAdminNews),
        deleteAdminNews: builder.mutation<
            { success: boolean; message: string },
            number
        >(newsEndpoints.deleteAdminNews),
        uploadAdminNewsImage: builder.mutation<
            { success: boolean; url: string; path: string },
            File
        >(newsEndpoints.uploadAdminNewsImage),
        getAdminNewsCategories: builder.query<any[], void>(
            newsEndpoints.getAdminNewsCategories
        ),
        createAdminNewsCategory: builder.mutation<any, any>(
            newsEndpoints.createAdminNewsCategory
        ),
        updateAdminNewsCategory: builder.mutation<any, { id: number; data: any }>(
            newsEndpoints.updateAdminNewsCategory
        ),
        deleteAdminNewsCategory: builder.mutation<{ success: boolean }, number>(
            newsEndpoints.deleteAdminNewsCategory
        ),

        // ==================== Programs Endpoints ====================
        getPrograms: builder.query<any[], void>(programsEndpoints.getPrograms),
        createProgram: builder.mutation<
            { success: boolean; message: string },
            Omit<import("./programs").Program, "id">
        >(programsEndpoints.createProgram),
        updateProgram: builder.mutation<
            { success: boolean; message: string },
            { id: number } & Omit<import("./programs").Program, "id">
        >(programsEndpoints.updateProgram),
        deleteProgram: builder.mutation<
            { success: boolean; message: string },
            number
        >(programsEndpoints.deleteProgram),
        getProgramSets: builder.query<any[], void>(
            programsEndpoints.getProgramSets
        ),
        createProgramSet: builder.mutation<
            { success: boolean; message: string },
            {
                name: string;
                description: string;
                price_enter: number;
                programs: number[];
            }
        >(programsEndpoints.createProgramSet),
        updateProgramSet: builder.mutation<
            { success: boolean; message: string },
            {
                id: number;
                name: string;
                description: string;
                price_enter: number;
                programs: number[];
            }
        >(programsEndpoints.updateProgramSet),
        deleteProgramSet: builder.mutation<
            { success: boolean; message: string },
            number
        >(programsEndpoints.deleteProgramSet),

        // ==================== Withdrawals Endpoints ====================
        getPendingWithdrawals: builder.query<
            { data: { withdrawals: import("./withdrawals").WithdrawalRequest[] } },
            void
        >({
            query: () => withdrawalsEndpoints.getPendingWithdrawals.query(),
            providesTags: ["PendingWithdrawals"],
        }),
        getWithdrawalHistory: builder.query<
            import("./withdrawals").WithdrawalHistoryResponse,
            {
                page?: number;
                per_page?: number;
                status?: string;
                source?: string;
                currency?: string;
                user_id?: number;
            } | void
        >(withdrawalsEndpoints.getWithdrawalHistory),
        confirmWithdrawal: builder.mutation<
            { success: boolean; message: string },
            {
                request_id: number;
                currency: number;
                account?: string;
                card?: string;
            }
        >({
            queryFn: (body, api, extraOptions, baseQuery) => 
                withdrawalsEndpoints.confirmWithdrawal.queryFn(body, api, extraOptions, baseQuery),
            invalidatesTags: ["PendingWithdrawals"],
        }),
        cancelWithdrawal: builder.mutation<
            { success: boolean; message: string },
            { user_id: number; currency: number; comment: string }
        >({
            query: (body) => withdrawalsEndpoints.cancelWithdrawal.query(body),
            invalidatesTags: ["PendingWithdrawals"],
        }),
        getPendingAlnyWithdrawals: builder.query<{ data: any[] }, void>(
            withdrawalsEndpoints.getPendingAlnyWithdrawals
        ),
        confirmAlnyWithdrawal: builder.mutation<
            { success: boolean; message: string },
            { user_id: number; sum?: number }
        >(withdrawalsEndpoints.confirmAlnyWithdrawal),
        cancelAlnyWithdrawal: builder.mutation<
            { success: boolean; message: string },
            { user_id: number; comment?: string }
        >(withdrawalsEndpoints.cancelAlnyWithdrawal),
        freezeWithdrawal: builder.mutation<
            { success: boolean; message: string },
            { id: number }
        >({
            query: (body) => withdrawalsEndpoints.freezeWithdrawal.query(body),
            invalidatesTags: ["PendingWithdrawals"],
        }),

        // ==================== API Stats Endpoints ====================
        getMyApiStats: builder.query<
            any,
            { limit?: number; has_errors?: boolean }
        >(apiStatsEndpoints.getMyApiStats),
        getAllApiStats: builder.query<
            any,
            {
                user_id?: number;
                endpoint?: string;
                limit?: number;
                offset?: number;
                has_errors?: boolean;
            }
        >(apiStatsEndpoints.getAllApiStats),
        getTopEndpoints: builder.query<any, { limit?: number }>(
            apiStatsEndpoints.getTopEndpoints
        ),
        getTopUsers: builder.query<
            any,
            { limit?: number; from_date?: string; to_date?: string }
        >(apiStatsEndpoints.getTopUsers),

        // ==================== Safe Statistics Endpoints ====================
        getAdminSafeStatistics: builder.query<
            { success: boolean; data: SafeStatistics },
            void
        >(safeEndpoints.getAdminSafeStatistics),
        getAdminSafeUsers: builder.query<
            { success: boolean; data: SafeUsersResponse },
            SafeUsersRequest
        >(safeEndpoints.getAdminSafeUsers),
        getAdminSafeUserDetails: builder.query<
            { success: boolean; data: SafeUserDetails },
            number
        >(safeEndpoints.getAdminSafeUserDetails),
        updateStructuralTopupPaid: builder.mutation<
            { success: boolean; message: string; data: { structural_topup_paid: boolean } },
            { userId: number; programId: number; paid: boolean }
        >(safeEndpoints.updateStructuralTopupPaid),

        // ==================== Garant Deals ====================

        // ==================== User Stats Endpoints ====================
        getUserStats: builder.query<{ success: boolean; data: import("../types/user").UserFullStats }, number>(
            userStatsEndpoints.getUserStats
        ),
        updateUserStats: builder.mutation<
            { success: boolean; data: import("../types/user").UserFullStats },
            { userId: number; data: Partial<import("../types/user").UserFullStats> }
        >(userStatsEndpoints.updateUserStats),

        // ==================== Star Control Endpoints ====================
        getStarControlUsers: builder.query<
            StarControlApiResponse,
            StarControlParams | void
        >(starControlEndpoints.getStarControlUsers),

        // ==================== Charity Control Endpoints ====================
        getCharityControlUsers: builder.query<
            CharityControlResponse,
            CharityControlParams
        >(charityControlEndpoints.getCharityControlUsers),

        // ==================== Program Compare (Structural vs Star) ====================
        getProgramCompare: builder.query<
            ProgramCompareResponse,
            ProgramCompareParams | void
        >(programCompareEndpoints.getProgramCompare),
        updateStarPayDate: builder.mutation<
            UpdateStarPayDateResponse,
            { userId: number; user_pay_date: string | null }
        >(programCompareEndpoints.updateStarPayDate),

        // ==================== Bloggers ====================
        getBloggers: builder.query<BloggerListResponse, void>(
            bloggerEndpoints.getBloggers
        ),
        addBlogger: builder.mutation<BloggerMutationResponse, string>(
            bloggerEndpoints.addBlogger
        ),
        removeBlogger: builder.mutation<void, number>(
            bloggerEndpoints.removeBlogger
        ),
        updateBlogger: builder.mutation<
            BloggerMutationResponse,
            UpdateBloggerParams
        >(bloggerEndpoints.updateBlogger),
        getBloggerStats: builder.query<
            BloggerStatsResponse,
            BloggerStatsParams
        >(bloggerEndpoints.getBloggerStats),
        getBloggerInvitationChildren: builder.query<
            BloggerInvitationChildrenResponse,
            BloggerInvitationChildrenParams
        >(bloggerEndpoints.getBloggerInvitationChildren),
        getBloggerChartStats: builder.query<BloggerChartStatsResponse, void>(
            bloggerEndpoints.getBloggerChartStats
        ),

        // ==================== Team Members ====================
        getTeamMembers: builder.query<{ data: TeamMemberDto[] }, void>({
            ...teamMembersEndpoints.getTeamMembers,
            providesTags: ["TeamMembers"],
        }),
        createTeamMember: builder.mutation<
            { message: string; data: TeamMemberDto },
            TeamMemberPayload
        >({
            ...teamMembersEndpoints.createTeamMember,
            invalidatesTags: ["TeamMembers"],
        }),
        updateTeamMember: builder.mutation<
            { message: string; data: TeamMemberDto },
            { id: number; data: TeamMemberPayload }
        >({
            ...teamMembersEndpoints.updateTeamMember,
            invalidatesTags: ["TeamMembers"],
        }),
        deleteTeamMember: builder.mutation<{ message: string }, number>({
            ...teamMembersEndpoints.deleteTeamMember,
            invalidatesTags: ["TeamMembers"],
        }),
        getTeamDepartments: builder.query<{ data: TeamDepartmentDto[] }, void>({
            ...teamMembersEndpoints.getTeamDepartments,
            providesTags: ["TeamDepartments"],
        }),
        createTeamDepartment: builder.mutation<
            { message: string; data: TeamDepartmentDto },
            TeamDepartmentPayload
        >({
            ...teamMembersEndpoints.createTeamDepartment,
            invalidatesTags: ["TeamDepartments"],
        }),
        updateTeamDepartment: builder.mutation<
            { message: string; data: TeamDepartmentDto },
            { id: number; data: TeamDepartmentPayload }
        >({
            ...teamMembersEndpoints.updateTeamDepartment,
            invalidatesTags: ["TeamDepartments"],
        }),
        deleteTeamDepartment: builder.mutation<{ message: string }, number>({
            ...teamMembersEndpoints.deleteTeamDepartment,
            invalidatesTags: ["TeamDepartments"],
        }),

        // ==================== Salary Piggy Bank Endpoints ====================
        getSalaryPiggyBankUsers: builder.query<SalaryPiggyBankUsersResponse, SalaryPiggyBankUsersParams>(
            salaryPiggyBankEndpoints.getSalaryPiggyBankUsers
        ),
        getSalaryPiggyBankPayments: builder.query<SalaryPiggyBankPaymentsResponse, SalaryPiggyBankPaymentsParams>(
            salaryPiggyBankEndpoints.getSalaryPiggyBankPayments
        ),
        getSalaryPiggyBankSettings: builder.query<SalaryPiggyBankSettings, void>(
            salaryPiggyBankEndpoints.getSalaryPiggyBankSettings
        ),
        updateSalaryPiggyBankSettings: builder.mutation<{ message: string; settings: SalaryPiggyBankSettings }, SalaryPiggyBankSettings>({
            ...salaryPiggyBankEndpoints.updateSalaryPiggyBankSettings,
            invalidatesTags: ["SalaryPiggyBankSettings"],
        }),
        getSalaryPiggyBankMonthlyStats: builder.query<SalaryPiggyBankMonthlyStats, void>(
            salaryPiggyBankEndpoints.getSalaryPiggyBankMonthlyStats
        ),
        getSalaryPiggyBankHistoricalStats: builder.query<SalaryPiggyBankHistoricalStatsResponse, void>(
            salaryPiggyBankEndpoints.getSalaryPiggyBankHistoricalStats
        ),
        removeSalaryPiggyBankUser: builder.mutation<{ message: string }, number>({
            ...salaryPiggyBankEndpoints.removeSalaryPiggyBankUser,
            invalidatesTags: ["SalaryPiggyBank"],
        }),
        restoreSalaryPiggyBankUser: builder.mutation<{ message: string }, number>({
            ...salaryPiggyBankEndpoints.restoreSalaryPiggyBankUser,
            invalidatesTags: ["SalaryPiggyBank"],
        }),

        // ==================== Advertising Fund Endpoints ====================
        getAdvFundSettings: builder.query<AdvFundAdminSettings, void>({
            ...advertisingFundEndpoints.getAdvFundSettings,
            providesTags: ["AdvFundSettings"],
        }),
        updateAdvFundSettings: builder.mutation<{ message: string }, AdvFundAdminSettings>({
            ...advertisingFundEndpoints.updateAdvFundSettings,
            invalidatesTags: ["AdvFundSettings"],
        }),
        getAdvFundUsers: builder.query<AdvFundUsersResponse, AdvFundUsersParams>({
            ...advertisingFundEndpoints.getAdvFundUsers,
            providesTags: ["AdvFund"],
        }),
        getAdvFundPayments: builder.query<AdvFundPaymentsResponse, AdvFundPaymentsParams>({
            ...advertisingFundEndpoints.getAdvFundPayments,
            providesTags: ["AdvFund"],
        }),
        getAdvFundMonthlyStats: builder.query<AdvFundMonthlyStats, void>({
            ...advertisingFundEndpoints.getAdvFundMonthlyStats,
            providesTags: ["AdvFund"],
        }),
        getAdvFundHistoricalStats: builder.query<AdvFundHistoricalStatsResponse, void>({
            ...advertisingFundEndpoints.getAdvFundHistoricalStats,
            providesTags: ["AdvFund"],
        }),
        getAdvFundChannels: builder.query<AdvFundChannel[], void>({
            ...advertisingFundEndpoints.getAdvFundChannels,
            providesTags: ["AdvFundChannels"],
        }),
        createAdvFundChannel: builder.mutation<{ message: string }, CreateAdvFundChannelRequest>({
            ...advertisingFundEndpoints.createAdvFundChannel,
            invalidatesTags: ["AdvFundChannels"],
        }),
        updateAdvFundChannel: builder.mutation<{ message: string }, UpdateAdvFundChannelRequest & { id: number }>({
            ...advertisingFundEndpoints.updateAdvFundChannel,
            invalidatesTags: ["AdvFundChannels"],
        }),
        deleteAdvFundChannel: builder.mutation<{ message: string }, number>({
            ...advertisingFundEndpoints.deleteAdvFundChannel,
            invalidatesTags: ["AdvFundChannels"],
        }),
        getAdvFundChannelExpenses: builder.query<AdvFundExpenseRecord[], number>(
            advertisingFundEndpoints.getAdvFundChannelExpenses
        ),
        getAdvFundExpenses: builder.query<AdvFundExpensesResponse, AdvFundExpensesParams>({
            ...advertisingFundEndpoints.getAdvFundExpenses,
            providesTags: ["AdvFund"],
        }),
        createAdvFundExpense: builder.mutation<{ message: string }, CreateAdvFundExpenseRequest & { channelId: number }>({
            ...advertisingFundEndpoints.createAdvFundExpense,
            invalidatesTags: ["AdvFund", "AdvFundChannels"],
        }),
        getAdvFundChannelClickTimeline: builder.query<import("./advertisingFund").AdvFundClickTimelineRecord[], number>(
            advertisingFundEndpoints.getAdvFundChannelClickTimeline
        ),
        // ==================== Help (Knowledge Base) Endpoints ====================
        getHelpTree: builder.query<HelpTreeNode[], HelpTreeParams | void>({
            query: (params) => ({
                url: "/admin/help/tree",
                params: (params || {}) as Record<string, any>,
            }),
            providesTags: ["HelpTree"],
        }),
        getHelpNode: builder.query<HelpNodeDetail, number>({
            query: (id) => helpEndpoints.getHelpNode.query(id),
            providesTags: ["HelpNodes"],
        }),
        createHelpNode: builder.mutation<HelpNodeDetail, HelpNodePayload>({
            query: (payload) => helpEndpoints.createHelpNode.query(payload),
            invalidatesTags: ["HelpTree", "HelpNodes"],
        }),
        updateHelpNode: builder.mutation<
            HelpNodeDetail,
            { id: number; data: Partial<HelpNodePayload> }
        >({
            query: (args) => helpEndpoints.updateHelpNode.query(args),
            invalidatesTags: ["HelpTree", "HelpNodes"],
        }),
        deleteHelpNode: builder.mutation<{ success?: boolean }, number>({
            query: (id) => helpEndpoints.deleteHelpNode.query(id),
            invalidatesTags: ["HelpTree", "HelpNodes"],
        }),
    }),
});

// Экспортируем хуки
export const {
    // Auth hooks
    useLoginMutation,
    useLoginAdminViaTelegramMutation,
    useRegisterMutation,
    useGetRegisterInfoQuery,
    useGetCurrentUserQuery,
    useCrossAuthMutation,
    useUpdateProfileMutation,
    useUpdateLanguageMutation,
    useDeletePhotoMutation,
    useVerify2FAMutation,

    // Webhook hooks
    useGetWebhooksQuery,
    useGetWebhookQuery,
    useCreateWebhookMutation,
    useUpdateWebhookMutation,
    useDeleteWebhookMutation,
    useGetWebhookLogsQuery,
    useTestWebhookMutation,
    useToggleWebhookStatusMutation,
    useGetWebhookEventTypesQuery,
    useGetWebhookStatsQuery,
    useGetWebhookEndpointsQuery,
    useGetWebhookEndpointQuery,
    useCreateWebhookEndpointMutation,
    useUpdateWebhookEndpointMutation,
    useDeleteWebhookEndpointMutation,
    useGetWebhookEndpointLogsQuery,
    useTestWebhookEndpointMutation,
    useToggleWebhookEndpointStatusMutation,
    useGetWebhookEndpointStatsQuery,
    useGetWebhookEndpointsByWebhookQuery,
    useCreateWebhookEndpointForWebhookMutation,
    useBulkCreateWebhookEndpointsMutation,

    // Payment Statistics hooks
    useGetPaymentLogsQuery,
    useGetPaymentLogDetailQuery,
    useGetPaymentStatisticsQuery,
    useGetUserPaymentStatsQuery,
    useExportPaymentLogsQuery,

    // Admin hooks
    useTopUpUserMutation,
    useTopUpUserTokenMutation,
    useTopUpCharityMutation,
    useGiftSharesMutation,
    useManualPayCreditSharesMutation,
    useGetSharesStatisticsQuery,
    useGetSharesSettingsQuery,
    useUpdateSharesSettingsMutation,
    useGetPurchasedUserSharesQuery,
    useGetGiftedUserSharesQuery,
    useGetGiftedUserSharesByUserIdQuery,
    useGetPurchasedUserSharesByUserIdQuery,
    useDeletePurchasedSharesMutation,
    useDeleteGiftedSharesMutation,
    useTransferSharesMutation,
    useTransferCreditSharesMutation,
    useGetCreditSharesQuery,
    useGetCreditShareQuery,
    useCreateCreditShareMutation,
    useUpdateCreditShareMutation,
    useDeleteCreditShareMutation,
    useGetPatronsQuery,
    useGetPatronageDetailsQuery,
    useUpdatePatronageMutation,
    useAddDailyPercentMutation,
    useChangeUserPasswordMutation,
    useAuthorizeInvestmentUserMutation,
    useConfirmWithdrawAllMutation,
    useConfirmTransferAllMutation,
    useGetInvestmentWithdrawRequestsQuery,
    useGetInvestmentTransferRequestsQuery,
    useGetDailyPercentStatsQuery,
    useGetWeeklyPercentStatsQuery,
    useGetInvestmentsQuery,
    useGetInvestmentStrategyQuery,
    useUpdateInvestmentStrategyMutation,
    useDirectWithdrawMutation,
    useConfirmInvestmentWithdrawMutation,
    useCancelInvestmentWithdrawMutation,
    useConfirmInvestmentTransferMutation,
    useCancelInvestmentTransferMutation,
    useGoldControlMutation,
    useGetTelegramBotsQuery,
    useTranslateMessagingMessageMutation,
    useSendMillenniumNewsMutation,
    useSendMillenniumBroadcastMutation,
    useListMillenniumBroadcastsQuery,
    useShowMillenniumBroadcastQuery,
    useEndMillenniumBroadcastMutation,
    useStartMillenniumBroadcastMutation,
    usePatchMillenniumBroadcastMutation,
    useDestroyMillenniumBroadcastMutation,
    useSendMassMessageMutation,
    useGetBatchStatusQuery,
    useCancelMassMessageBatchMutation,
    useGetMessagingCampaignsQuery,
    useStoreMessagingCampaignMutation,
    useUpdateMessagingCampaignMutation,
    useCancelMessagingCampaignMutation,
    useDeleteMessagingCampaignMutation,
    useClearMessagingHistoryMutation,
    useGetPendingJivoQuery,
    useConfirmJivoMutation,
    usePayMarathonMutation,
    useRestoreMarathonUserAndTicketsMutation,
    useGetStrategiesQuery,
    useUpdateStrategyMutation,

    // Search hooks
    useSearchUsersQuery,
    useSearchCharityQuery,

    // Notification hooks
    useGetNotificationsQuery,
    useMarkAsReadMutation,
    useMarkAllAsReadMutation,
    useGetUnreadCountQuery,
    useDeleteNotificationMutation,

    // Marketplace hooks
    useGetMarketplaceUrlQuery,

    // Bingx hooks
    useGetPendingBingxQuery,
    useLazyGetPendingBingxQuery,
    useConfirmBingxMutation,
    useCancelBingxRequestMutation,
    useGetBingXWalletsQuery,
    useCreateBingXWalletMutation,
    useUpdateBingXWalletMutation,
    useDeleteBingXWalletMutation,
    useSyncBingXAddressesMutation,
    useLazyGetBingXBalanceQuery,
    useLazyGetBingXWithdrawalsQuery,
    useGetBingXTopupsQuery,
    useToggleBingXAddressMutation,
    useAddBingXAddressMutation,
    useResetBingXSumTotalMutation,

    // Financial hooks
    useGetFinancialStatsQuery,
    useGetPaymentIncomeStatsQuery,
    useGetWithdrawalStatsQuery,
    useGetAllUsersStatsQuery,
    useGetGameStatsQuery,
    useGetGameVipStatsQuery,
    useGetPaymentIncomeListQuery,
    useGetPaymentIncomeDetailsQuery,
    useGetAdamProxyPaymentStatsQuery,
    useGetAdamProxyPaymentListQuery,
    useGetAdamProxyPayoutListQuery,
    useCreateAdamProxyPayoutMutation,
    useGetStripePaymentStatsQuery,
    useGetStripePaymentListQuery,
    useGetTributePaymentSettingsQuery,
    useUpdateTributePaymentSettingsMutation,
    useResetTributeStatsMutation,
    useGetTributePaymentStatsQuery,
    useGetTributePaymentListQuery,
    useGetTributeWithdrawalsQuery,
    useCreateTributeWithdrawalMutation,
    useUpdateTributeWithdrawalMutation,
    useGetAdminAltynBalanceQuery,
    useGetAdminAltynStatsQuery,
    useGetAdminAltynPaymentsQuery,
    useGetAdminAltynClientsQuery,
    useGetAdminAltynPaymentQuery,
    useGetAdminAltynPayoutsQuery,
    useGetAdminAltynPayoutQuery,
    useCheckAdminAltynPayoutMutation,
    useCreateAdminAltynCryptoPayoutMutation,
    useCreateAdminAltynSbpPayoutMutation,
    useCreateAltynCryptoPayoutMutation,

    // FriendlyPay hooks
    useGetAdminFriendlyPaySettingsQuery,
    useUpdateAdminFriendlyPaySettingsMutation,
    useGetWithdrawalReceiptSettingsQuery,
    useUpdateWithdrawalReceiptSettingsMutation,
    useGetAdminFriendlyPayStatsQuery,
    useGetAdminFriendlyPayPaymentsQuery,
    useGetAdminFriendlyPayPaymentQuery,

    // Tickets hooks
    useGetAdminTicketsStatsQuery,
    useGetAdminTicketsQuery,
    useAwardTicketsMutation,

    // Token hooks
    usePayBlagoTourizmTokensMutation,
    // Game token operations
    useGetTokenOperationsQuery,
    useGetTokenOperationsActionsQuery,

    // User Stats hooks
    useGetUserStatsQuery,
    useUpdateUserStatsMutation,

    // Marathon statistics hooks
    useGetMarathonStatisticsQuery,
    useGetMarathonTypesQuery,
    useImportMarathonResultsMutation,
    useClearMarathonStatisticsMutation,
    useExportMarathonStatisticsQuery,
    useLazyExportMarathonStatisticsQuery,
    useGetTicketsForExportQuery,
    useLazyGetTicketsForExportQuery,

    // Tree/Structure hooks
    useCheckTreeIntegrityMutation,
    useGetTreeUserDetailsMutation,

    // Data Integrity hooks
    useVerifyStatsIntegrityMutation,
    useFixStatsIntegrityUserMutation,
    useGetStatsIntegrityUserDetailsQuery,

    // Admin News hooks
    useGetAdminNewsQuery,
    useGetAdminNewsItemQuery,
    useCreateAdminNewsMutation,
    useUpdateAdminNewsMutation,
    useDeleteAdminNewsMutation,
    useUploadAdminNewsImageMutation,
    useGetAdminNewsCategoriesQuery,
    useCreateAdminNewsCategoryMutation,
    useUpdateAdminNewsCategoryMutation,
    useDeleteAdminNewsCategoryMutation,

    // Withdrawals hooks
    useGetPendingWithdrawalsQuery,
    useLazyGetPendingWithdrawalsQuery,
    useConfirmWithdrawalMutation,
    useCancelWithdrawalMutation,
    useFreezeWithdrawalMutation,
    useGetPendingAlnyWithdrawalsQuery,
    useLazyGetPendingAlnyWithdrawalsQuery,
    useConfirmAlnyWithdrawalMutation,
    useCancelAlnyWithdrawalMutation,
    useGetWithdrawalHistoryQuery,

    // API Stats hooks
    useGetMyApiStatsQuery,
    useGetAllApiStatsQuery,
    useGetTopEndpointsQuery,
    useGetTopUsersQuery,

    // Programs hooks
    useGetProgramsQuery,
    useCreateProgramMutation,
    useUpdateProgramMutation,
    useDeleteProgramMutation,
    useGetProgramSetsQuery,
    useCreateProgramSetMutation,
    useUpdateProgramSetMutation,
    useDeleteProgramSetMutation,

    // Safe Statistics hooks
    useGetAdminSafeStatisticsQuery,
    useGetAdminSafeUsersQuery,
    useGetAdminSafeUserDetailsQuery,
    useUpdateStructuralTopupPaidMutation,

    // Star Control hooks
    useGetStarControlUsersQuery,

    // Charity Control hooks
    useGetCharityControlUsersQuery,

    // Program compare hooks
    useGetProgramCompareQuery,
    useUpdateStarPayDateMutation,

    // Bloggers hooks
    useGetBloggersQuery,
    useAddBloggerMutation,
    useRemoveBloggerMutation,
    useUpdateBloggerMutation,
    useGetBloggerStatsQuery,
    useGetBloggerInvitationChildrenQuery,
    useLazyGetBloggerChartStatsQuery,

    // Salary Piggy Bank hooks
    useGetSalaryPiggyBankUsersQuery,
    useGetSalaryPiggyBankPaymentsQuery,
    useGetSalaryPiggyBankSettingsQuery,
    useUpdateSalaryPiggyBankSettingsMutation,
    useGetSalaryPiggyBankMonthlyStatsQuery,
    useGetSalaryPiggyBankHistoricalStatsQuery,
    useRemoveSalaryPiggyBankUserMutation,
    useRestoreSalaryPiggyBankUserMutation,

    // Advertising Fund hooks
    useGetAdvFundSettingsQuery,
    useUpdateAdvFundSettingsMutation,
    useGetAdvFundUsersQuery,
    useGetAdvFundPaymentsQuery,
    useGetAdvFundMonthlyStatsQuery,
    useGetAdvFundHistoricalStatsQuery,
    useGetAdvFundChannelsQuery,
    useCreateAdvFundChannelMutation,
    useUpdateAdvFundChannelMutation,
    useDeleteAdvFundChannelMutation,
    useGetAdvFundExpensesQuery,
    useGetAdvFundChannelExpensesQuery,
    useCreateAdvFundExpenseMutation,
    useGetAdvFundChannelClickTimelineQuery,

    // Incentive Fund hooks
    useGetIncentiveFundSummaryQuery,
    useGetIncentiveFundPaymentsQuery,
    useGetIncentiveFundPayoutsQuery,
    useCreateIncentiveFundPayoutMutation,

    // Team members hooks
    useGetTeamMembersQuery,
    useCreateTeamMemberMutation,
    useUpdateTeamMemberMutation,
    useDeleteTeamMemberMutation,
    useGetTeamDepartmentsQuery,
    useCreateTeamDepartmentMutation,
    useUpdateTeamDepartmentMutation,
    useDeleteTeamDepartmentMutation,

    // Help (Knowledge Base) hooks
    useGetHelpTreeQuery,
    useGetHelpNodeQuery,
    useCreateHelpNodeMutation,
    useUpdateHelpNodeMutation,
    useDeleteHelpNodeMutation,
    // Game Bonus hooks
    useGetGameBonusSummaryQuery,
    useGetGameBonusHistoryQuery,
    useGetGameBonusPayoutsQuery,
    useCreateGameBonusPayoutMutation,
    useGetGameBonusGlobalStatsQuery,
    useGetAggregatedStrategyStatsQuery,

    // Landing Analytics hooks
    useGetLandingAnalyticsEventsQuery,
    useGetLandingAnalyticsStatsQuery,
    useGetLandingAnalyticsFilterOptionsQuery,
    useGetLandingAnalyticsChartQuery,
    useGetLandingAnalyticsVideoStatsQuery,
} = api;

// Re-export types
export type {
    CreditShare,
    CreateCreditShareRequest,
    UpdateCreditShareRequest,
    CreditShareUser,
    InvestmentListItem,
    GetInvestmentsParams,
    GetInvestmentsResponse,
    InvestmentStrategyData,
    GetInvestmentStrategyParams,
    GetInvestmentStrategyResponse,
    UpdateInvestmentStrategyParams,
    UpdateInvestmentStrategyResponse,
    InvestmentDailyStat,
    InvestmentWeeklyStat,
    InvestmentStatsSummary,
    GetInvestmentStatsParams,
    GetInvestmentDailyStatsResponse,
    GetInvestmentWeeklyStatsResponse,
    InvestmentStrategy,
    UpdateInvestmentStrategyPayload,
    AggregatedStrategyStat,
    GetAggregatedStrategyStatsResponse,
} from "./admin";

export type { HelpTreeNode, HelpNodeDetail, HelpNodePayload } from "./help";
